import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'

import { H3, P, Text } from '@mxenabled/mxui'

import { Drawer, Loader, useGlobalCopyStore, useGlobalUiStore } from '../../common'

import HealthChart from './HealthChart'

interface ScoreRubricDrawerProps {
  isOpen: boolean
  onClose: () => void
}

const ScoreRubricDrawer = ({ isOpen, onClose }: ScoreRubricDrawerProps) => {
  const { finstrong: copy } = useGlobalCopyStore()
  const { isCopyLoaded } = useGlobalUiStore()

  if (!isCopyLoaded) return <Loader />

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title={copy.score_rubric}>
      <Stack sx={{ bgcolor: 'background.default', gap: 36, height: '100%', p: 24 }}>
        <Stack sx={{ gap: 20 }}>
          <Stack sx={{ gap: 8 }}>
            <H3>{copy.your_path_to_financial_freedom}</H3>
            <P variant="subtitle1">{copy.finstrong_freedom_description}</P>
          </Stack>
          <Box>
            <HealthChart copy={copy} />
          </Box>
        </Stack>
        <Stack sx={{ gap: 12 }}>
          <Text sx={{ fontWeight: 600 }} variant="body1">
            {copy.how_do_we_do_this}
          </Text>
          <List
            component="ol"
            sx={{
              listStyleType: 'decimal',
              pl: 20,
              pr: 16,
              '& li': {
                '::marker': {
                  fontWeight: 600,
                },
              },
              '& .MuiListItemText-secondary': {
                fontSize: 15,
                lineHeight: '24px',
                pt: 4,
              },
            }}
          >
            <ListItem sx={{ display: 'list-item' }}>
              <ListItemText
                primary={copy.understand_your_habits}
                secondary={copy.understand_your_habits_description}
              />
            </ListItem>
            <ListItem sx={{ display: 'list-item', pt: 16 }}>
              <ListItemText
                primary={copy.see_whats_possible}
                secondary={copy.see_whats_possible_description}
              />
            </ListItem>
          </List>
        </Stack>
        <Stack sx={{ gap: 4 }}>
          <Text sx={{ fontWeight: 600, pb: 4 }} variant="body1">
            {copy.we_ask_questions}
          </Text>
          <List
            component="ol"
            sx={{
              listStyleType: 'initial',
              pl: 20,
              pr: 16,
              '& li': {
                minHeight: 'max-content',
                '::marker': {
                  color: 'text.secondary',
                  fontWeight: 600,
                },
              },
              '& .MuiListItemText-secondary': {
                fontSize: 15,
                lineHeight: '24px',
              },
            }}
          >
            <ListItem sx={{ display: 'list-item' }}>
              <ListItemText secondary={copy.we_ask_questions_description_1} />
            </ListItem>
            <ListItem sx={{ display: 'list-item' }}>
              <ListItemText secondary={copy.we_ask_questions_description_2} />
            </ListItem>
          </List>
        </Stack>
      </Stack>
    </Drawer>
  )
}

export default observer(ScoreRubricDrawer)
